import { useEffect, useRef, useCallback, useState } from 'react';
import { cn } from '@/lib/utils';
import { generateId } from '@/lib/utils';
import { useGameScale, GAME_DESIGN } from '@/hooks/useGameScale';
import type { GameState, Target } from './types';
import { 
  GAME_CONFIG, 
  TARGET_CONFIGS, 
  SPAWN_CHANCES, 
  BASE_SIZES 
} from './types';

interface GameScreenProps {
  gameState: GameState;
  onGameEnd: (state: GameState) => void;
  updateGameState: (updates: Partial<GameState>) => void;
}

export function GameScreen({ gameState, onGameEnd, updateGameState }: GameScreenProps) {
  const screenRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const gameStateRef = useRef(gameState);
  const [targets, setTargets] = useState<Target[]>([]);
  const [effects, setEffects] = useState<Array<{ id: string; x: number; y: number; text: string; type: 'good' | 'bad' | 'special' }>>([]);
  const [levelBanner, setLevelBanner] = useState<number | null>(null);
  
  const spawnIntervalRef = useRef<number>(GAME_CONFIG.spawnIntervalStartMs);
  const spawnTimerRef = useRef<number | null>(null);
  const gameTimerRef = useRef<number | null>(null);
  const logoSpawnedRef = useRef(false);
  
  // Responsive scaling
  const { scaleValue, dimensions } = useGameScale(containerRef, GAME_DESIGN.width, GAME_DESIGN.height);

  // Keep ref in sync
  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Spawn target with responsive scaling
  const spawnTarget = useCallback((forceLogo = false) => {
    if (gameStateRef.current.status !== 'playing') return;

    const screen = screenRef.current;
    if (!screen) return;

    let configKey: string;
    if (forceLogo) {
      configKey = 'logo';
    } else {
      // Random chance for logo
      const chance = Math.floor(Math.random() * 20);
      if (chance === 0 && !logoSpawnedRef.current && gameStateRef.current.timeRemaining < GAME_CONFIG.gameTimeMs - 5000) {
        configKey = 'logo';
        logoSpawnedRef.current = true;
      } else {
        configKey = SPAWN_CHANCES[Math.floor(Math.random() * SPAWN_CHANCES.length)]!;
      }
    }

    const config = TARGET_CONFIGS[configKey];
    if (!config) return;

    // Get base size from design
    const sizeGroup = config.category === 'good' ? 'cabinet' : config.category === 'bad' ? 'vario' : 'logo';
    const sizeKey = config.size || 'm';
    const sizeMap = BASE_SIZES[sizeGroup as keyof typeof BASE_SIZES];
    const baseSize = (sizeMap && sizeKey in sizeMap ? sizeMap[sizeKey as keyof typeof sizeMap] : null) || { w: 80, h: 80 };

    // Scale by level (game difficulty scaling)
    const level = gameStateRef.current.level;
    const cabinetScale = Math.max(0.4, 1 - 0.1 * (level - 1));
    const varioScale = 1 + 0.1 * (level - 1);
    const levelScale = sizeGroup === 'cabinet' ? cabinetScale : sizeGroup === 'vario' ? varioScale : 1;

    // Apply both level scaling and responsive scaling
    const screenScale = Math.min(screen.offsetWidth / GAME_DESIGN.width, screen.offsetHeight / GAME_DESIGN.height, 1);
    const width = baseSize.w * levelScale * screenScale;
    const height = baseSize.h * levelScale * screenScale;

    // Position
    const screenWidth = screen.offsetWidth;
    const screenHeight = screen.offsetHeight;
    
    let x: number, y: number, animationDuration: number;

    if (config.animation === 'pop-up') {
      const minX = GAME_CONFIG.machineArea.minX * screenWidth;
      const maxX = GAME_CONFIG.machineArea.maxX * screenWidth;
      const minY = GAME_CONFIG.machineArea.minY * screenHeight;
      const maxY = GAME_CONFIG.machineArea.maxY * screenHeight;
      
      x = minX + Math.random() * Math.max(10, maxX - minX - width);
      y = minY + Math.random() * Math.max(10, maxY - minY - height);
      animationDuration = (Math.random() * 1.0 + 2.0) * 1000;
    } else {
      // fly-by - scale the offscreen start position
      const flyOffset = scaleValue(150);
      x = Math.random() > 0.5 ? -flyOffset : screenWidth + flyOffset;
      y = screenHeight * 0.15 + Math.random() * (screenHeight * 0.25);
      animationDuration = (Math.random() * 2 + 4) * 1000;
    }

    const target: Target = {
      id: generateId(),
      config,
      x,
      y,
      width,
      height,
      isHit: false,
      animationDuration,
      createdAt: Date.now(),
    };

    setTargets(prev => [...prev, target]);

    // Remove target after animation
    setTimeout(() => {
      setTargets(prev => {
        const t = prev.find(p => p.id === target.id);
        if (t && !t.isHit && t.config.category === 'good') {
          // Missed cabinet
          updateGameState({ 
            missedCabinets: gameStateRef.current.missedCabinets + 1 
          });
          
          // Check game over
          if (gameStateRef.current.missedCabinets + 1 >= GAME_CONFIG.maxMissedCabinets) {
            onGameEnd(gameStateRef.current);
          }
        }
        return prev.filter(p => p.id !== target.id);
      });
    }, animationDuration);
  }, [updateGameState, onGameEnd, scaleValue]);

  // Handle target hit
  const handleTargetHit = useCallback((target: Target, x: number, y: number) => {
    const { config } = target;
    
    // Mark as hit
    setTargets(prev => prev.map(t => 
      t.id === target.id ? { ...t, isHit: true } : t
    ));

    // Update score
    if (config.category === 'good') {
      const newScore = gameStateRef.current.score + config.points;
      const newHits = gameStateRef.current.hitCabinets + 1;
      updateGameState({ score: newScore, hitCabinets: newHits });
      setEffects(prev => [...prev, { id: generateId(), x, y, text: `+${config.points}`, type: 'good' }]);
    } else if (config.category === 'bad') {
      const newScore = Math.max(0, gameStateRef.current.score - config.penalty);
      updateGameState({ score: newScore });
      setEffects(prev => [...prev, { id: generateId(), x, y, text: `-${config.penalty}`, type: 'bad' }]);
    } else if (config.category === 'special') {
      const newScore = gameStateRef.current.score + config.points;
      updateGameState({ 
        score: newScore, 
        autoAimActive: true, 
        autoAimRemaining: GAME_CONFIG.autoAimDurationMs 
      });
      setEffects(prev => [...prev, { id: generateId(), x, y, text: `+${config.points}`, type: 'special' }]);
    }

    // Remove after hit animation
    setTimeout(() => {
      setTargets(prev => prev.filter(t => t.id !== target.id));
    }, 1000);
  }, [updateGameState]);

  // Handle click/tap
  const handleClick = useCallback((clientX: number, clientY: number) => {
    if (gameStateRef.current.status !== 'playing') return;

    const screen = screenRef.current;
    if (!screen) return;

    const rect = screen.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Create spark effect
    setEffects(prev => [...prev, { id: generateId(), x, y, text: '', type: 'good' }]);
    setTimeout(() => {
      setEffects(prev => prev.filter(e => e.text !== ''));
    }, 300);

    // Find hit target (check from top/most recent)
    const hitTarget = [...targets].reverse().find(t => {
      if (t.isHit) return false;
      const targetRect = {
        left: t.x,
        right: t.x + t.width,
        top: t.y,
        bottom: t.y + t.height,
      };
      return x >= targetRect.left && x <= targetRect.right && 
             y >= targetRect.top && y <= targetRect.bottom;
    });

    // Auto-aim logic
    if (!hitTarget && gameStateRef.current.autoAimActive) {
      const goodTarget = targets.find(t => !t.isHit && t.config.category === 'good');
      if (goodTarget) {
        handleTargetHit(goodTarget, goodTarget.x + goodTarget.width / 2, goodTarget.y + goodTarget.height / 2);
        return;
      }
    }

    if (hitTarget) {
      handleTargetHit(hitTarget, x, y);
    }
  }, [targets, handleTargetHit]);

  // Mouse/touch handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleClick(e.clientX, e.clientY);
  }, [handleClick]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch) {
        handleClick(touch.clientX, touch.clientY);
      }
    }
  }, [handleClick]);

  // Game loop
  useEffect(() => {
    if (gameState.status !== 'playing') return;

    // Show level banner
    setLevelBanner(gameState.level);
    setTimeout(() => setLevelBanner(null), 1500);

    // Reset
    logoSpawnedRef.current = false;
    spawnIntervalRef.current = GAME_CONFIG.spawnIntervalStartMs;

    // Game timer
    const startTime = Date.now();
    gameTimerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, GAME_CONFIG.gameTimeMs - elapsed);
      
      updateGameState({ timeRemaining: remaining });

      // Update spawn interval
      if (spawnIntervalRef.current > GAME_CONFIG.spawnIntervalMinMs) {
        const decrement = (GAME_CONFIG.spawnIntervalStartMs - GAME_CONFIG.spawnIntervalMinMs) / (GAME_CONFIG.gameTimeMs / 1000);
        spawnIntervalRef.current = Math.max(GAME_CONFIG.spawnIntervalMinMs, spawnIntervalRef.current - decrement / 10);
      }

      // Spawn logo at mid-game
      if (!logoSpawnedRef.current && remaining <= GAME_CONFIG.gameTimeMs / 2 && remaining > GAME_CONFIG.gameTimeMs / 2 - 200) {
        spawnTarget(true);
        logoSpawnedRef.current = true;
      }

      // Next level
      if (remaining <= 0) {
        updateGameState({ level: gameStateRef.current.level + 1 });
        // This would trigger the useEffect again with new level
      }
    }, 100);

    // Spawn timer
    const scheduleSpawn = () => {
      spawnTimerRef.current = window.setTimeout(() => {
        spawnTarget();
        scheduleSpawn();
      }, spawnIntervalRef.current);
    };
    spawnTarget();
    scheduleSpawn();

    return () => {
      if (gameTimerRef.current) clearInterval(gameTimerRef.current);
      if (spawnTimerRef.current) clearTimeout(spawnTimerRef.current);
    };
  }, [gameState.status, gameState.level, spawnTarget, updateGameState]);

  // Auto-aim timer
  useEffect(() => {
    if (!gameState.autoAimActive) return;

    const timer = setInterval(() => {
      updateGameState({ 
        autoAimRemaining: Math.max(0, gameStateRef.current.autoAimRemaining - 100) 
      });
      
      if (gameStateRef.current.autoAimRemaining <= 0) {
        updateGameState({ autoAimActive: false });
      }
    }, 100);

    return () => clearInterval(timer);
  }, [gameState.autoAimActive, updateGameState]);

  // Clean up effects
  useEffect(() => {
    const cleanup = setInterval(() => {
      setEffects(prev => prev.filter(e => Date.now() - parseInt(e.id, 36) < 1000));
    }, 500);
    return () => clearInterval(cleanup);
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0">
      <div 
        ref={screenRef}
        className={cn(
          "absolute inset-0 overflow-hidden cursor-crosshair select-none",
          "bg-cover bg-center bg-no-repeat",
          gameState.autoAimActive && "ring-4 ring-ukonnx-green ring-inset animate-pulse-glow"
        )}
        style={{ 
          backgroundImage: 'url(/assets/cabinet-transformer/backgroundleer.png)',
          // Set CSS variable for fly animations
          '--container-width': `${dimensions.width}px`,
        } as React.CSSProperties}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Targets */}
        {targets.map(target => (
          <TargetElement 
            key={target.id} 
            target={target} 
            containerWidth={dimensions.width}
          />
        ))}

        {/* Click Effects - scaled */}
        {effects.map(effect => (
          <ClickEffect 
            key={effect.id} 
            {...effect} 
            scaleValue={scaleValue}
          />
        ))}

        {/* Level Banner - scaled */}
        {levelBanner && (
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
                        bg-bg-deep/90 text-text-primary rounded-full
                        font-bold border border-border-dark
                        animate-scale-in z-50"
            style={{
              padding: `${scaleValue(16)}px ${scaleValue(32)}px`,
              fontSize: Math.max(16, scaleValue(24)),
            }}
          >
            Level {levelBanner}
          </div>
        )}
      </div>
    </div>
  );
}

function TargetElement({ target, containerWidth }: { target: Target; containerWidth: number }) {
  const { config, x, y, width, height, isHit, animationDuration } = target;
  const isFlying = config.animation === 'fly-by';
  const flyDirection = x < 0 ? 'lr' : 'rl';

  return (
    <div
      className={cn(
        "absolute bg-contain bg-no-repeat bg-center cursor-pointer transition-transform",
        isHit && "pointer-events-none",
        isHit && config.category === 'good' && "animate-hit-good",
        isHit && config.category === 'bad' && "animate-hit-bad",
        isHit && config.category === 'special' && "scale-150 opacity-0",
        !isHit && !isFlying && "animate-pop-up",
        !isHit && isFlying && flyDirection === 'lr' && "animate-fly-lr",
        !isHit && isFlying && flyDirection === 'rl' && "animate-fly-rl",
      )}
      style={{
        left: x,
        top: y,
        width,
        height,
        backgroundImage: `url(${isHit && config.hitImage ? config.hitImage : config.image})`,
        animationDuration: `${animationDuration}ms`,
        '--container-width': `${containerWidth}px`,
      } as React.CSSProperties}
    />
  );
}

interface ClickEffectProps {
  x: number;
  y: number;
  text: string;
  type: 'good' | 'bad' | 'special';
  scaleValue: (px: number) => number;
}

function ClickEffect({ x, y, text, type, scaleValue }: ClickEffectProps) {
  if (!text) {
    // Spark effect - scaled
    const sparkSize = scaleValue(32);
    return (
      <div 
        className="absolute bg-ukonnx-green rounded-full pointer-events-none
                  animate-ping opacity-75"
        style={{ 
          left: x - sparkSize / 2, 
          top: y - sparkSize / 2,
          width: sparkSize,
          height: sparkSize,
          boxShadow: '0 0 20px 10px rgba(34, 197, 94, 0.6)',
        }}
      />
    );
  }

  const fontSize = type === 'special' ? scaleValue(30) : scaleValue(24);

  return (
    <div 
      className={cn(
        "absolute font-bold pointer-events-none animate-float-up",
        type === 'good' && "text-ukonnx-green",
        type === 'bad' && "text-accent-red",
        type === 'special' && "text-accent-gold",
      )}
      style={{ 
        left: x - scaleValue(30), 
        top: y - scaleValue(20),
        fontSize: Math.max(14, fontSize),
        textShadow: '1px 1px 3px rgba(0,0,0,0.7)',
      }}
    >
      {text}
    </div>
  );
}
